import { useCallback, useEffect, useRef, useState } from "react";

const MIN = 0.3;
const MAX = 2.4;
const PAD = 48;
const clamp = (k) => Math.min(MAX, Math.max(MIN, k));

/**
 * Pan and zoom for the map: wheel/trackpad pans, ⌘/Ctrl + wheel and pinch zoom around the pointer, dragging the
 * background pans. «Tilpass» fits everything that is open; «Tilbakestill» puts the actuator at the left in 100 %.
 */
export function useViewport(ref) {
  const [vp, setVp] = useState({ x: PAD, y: PAD, k: 1 });
  const drag = useRef(null);
  const pinch = useRef(null);
  const [dragging, setDragging] = useState(false);

  const zoomAt = useCallback((cx, cy, factor) => setVp((v) => { const k = clamp(v.k * factor); return { k, x: cx - (cx - v.x) * (k / v.k), y: cy - (cy - v.y) * (k / v.k) }; }), []);
  const zoomCenter = useCallback((factor) => { const el = ref.current; if (!el) return; zoomAt(el.clientWidth / 2, el.clientHeight / 2, factor); }, [ref, zoomAt]);
  const fit = useCallback((bbox) => {
    const el = ref.current; if (!el || !bbox) return;
    const W = el.clientWidth; const H = el.clientHeight;
    const k = clamp(Math.min((W - 2 * PAD) / bbox.w, (H - 2 * PAD) / bbox.h, 1.15));
    setVp({ k, x: (W - bbox.w * k) / 2 - bbox.x * k, y: (H - bbox.h * k) / 2 - bbox.y * k });
  }, [ref]);
  const reset = useCallback((bbox) => {
    const el = ref.current; if (!el) return;
    const H = el.clientHeight;
    setVp({ k: 1, x: PAD - (bbox?.x || 0), y: bbox ? Math.max(PAD, (H - bbox.h) / 2 - bbox.y) : PAD });
  }, [ref]);
  /** Keep a rectangle (map coordinates) inside the view – nudging, never jumping. */
  const reveal = useCallback((r) => {
    const el = ref.current; if (!el || !r) return;
    setVp((v) => {
      const W = el.clientWidth; const H = el.clientHeight;
      const x1 = r.x * v.k + v.x; const y1 = r.y * v.k + v.y; const x2 = x1 + r.w * v.k; const y2 = y1 + r.h * v.k;
      let { x, y } = v;
      if (x1 < PAD) x += PAD - x1; else if (x2 > W - PAD) x -= x2 - (W - PAD);
      if (y1 < PAD) y += PAD - y1; else if (y2 > H - PAD) y -= y2 - (H - PAD);
      return x === v.x && y === v.y ? v : { ...v, x, y };
    });
  }, [ref]);

  useEffect(() => {
    const el = ref.current; if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0105));
      else setVp((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ref, zoomAt]);

  const pointers = useRef(new Map());
  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y) };
      drag.current = null;
      return;
    }
    if (!e.target.closest("[data-bg]") || e.target.closest("[data-node]") || e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, vx: vp.x, vy: vp.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const r = ref.current.getBoundingClientRect();
      zoomAt((a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top, d / pinch.current.d);
      pinch.current.d = d;
      return;
    }
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.x; const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) > 3) { d.moved = true; setDragging(true); }
    if (d.moved) setVp((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (drag.current) { drag.current = null; setDragging(false); }
  };
  return { vp, setVp, zoomAt, zoomCenter, fit, reset, reveal, dragging, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
}
